"use client";

import dynamic from "next/dynamic";
import { docsHref } from "@/lib/docs-links";
import type { CSSProperties, ReactNode } from "react";
import {
  ACCENT_TEXT,
  DISPLAY,
  EDITORIAL_DARK_SURFACE,
  EDITORIAL_CONTAINER_CLS,
  GROUND,
  HOME_DEFAULT_THEME_ID,
  INK,
  LABEL_CLS,
  mix,
  SURFACE_DARK,
} from "./theme";
import { EditorialThemeProvider } from "./theme-context";
import { EditorialNav, type NavLink } from "./nav";
import { EditorialFooter, type FooterColumn } from "./footer";
import { Grain } from "./primitives";
import type { Cta } from "./types";

/**
 * Loaded lazily, and always mounted: the activation conversation is the primary
 * conversion on all deployments. Opens on any click of an
 * `a[href="#waitlist"]` (i.e. WAITLIST_HREF).
 */
const WaitlistDialog = dynamic(
  () =>
    import("@/components/editorial/waitlist-dialog").then(
      (module) => module.WaitlistDialog,
    ),
  { ssr: false },
);

const NewsletterDialog = dynamic(
  () =>
    import("@/components/editorial/newsletter-dialog").then(
      (module) => module.NewsletterDialog,
    ),
  { ssr: false },
);

/**
 * Primary conversion target for every activation CTA. Routes to the shared
 * contact dialog through the existing `#waitlist` hash on every deployment.
 */
export const WAITLIST_HREF = "#waitlist";
export const NEWSLETTER_HREF = "#newsletter";
export const PARTNER_HREF = "https://go.mlx.systems/partners";

/* One site-wide nav + footer config, shared by every editorial page. */
const SITE_NAV_LINKS: NavLink[] = [
  { label: "Why a Twin", href: "/#platform" },
  { label: "Pricing", href: "/pricing" },
  { label: "Research", href: "/research" },
  { label: "Blog", href: "/blog" },
  { label: "Changelog", href: "/changelog" },
  { label: "Docs", href: docsHref() },
  { label: "Become a partner", href: PARTNER_HREF },
];
const SITE_CTA: Cta = {
  label: "Discuss an activation",
  href: WAITLIST_HREF,
};
const SITE_FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "Why a Twin", href: "/#platform" },
      { label: "How it works", href: "/#how-it-works" },
      { label: "Pricing", href: "/pricing" },
      { label: "Accountants", href: "/for/accountants" },
      { label: "Consultancies", href: "/for/consultancies" },
      { label: "Changelog", href: "/changelog" },
      { label: "Research", href: "/research" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Blog", href: "/blog" },
      { label: "Research briefing", href: NEWSLETTER_HREF },
      { label: "Become a partner", href: PARTNER_HREF },
      { label: "Mercury Labs", href: "https://mercurylabs.io" },
    ],
  },
  {
    heading: "Support & legal",
    links: [
      { label: "Support", href: docsHref("/support") },
      { label: "Legal & Security", href: docsHref("/policies") },
      { label: "Privacy", href: docsHref("/policies/privacy") },
      { label: "Terms", href: docsHref("/policies/terms") },
      { label: "Cookie settings", href: "#cookie-settings" },
    ],
  },
];

/**
 * Site chrome wrapper — theme provider + nav + footer — shared by the
 * home and every sub-page so the editorial nav/footer are defined once.
 * `solidNav` forces the cream nav from the top (pages without a
 * full-height dark hero); leave false for the home's scroll-aware nav.
 */
export function EditorialShell({
  children,
  solidNav = false,
  provideTheme = true,
  dark = false,
}: {
  children: ReactNode;
  solidNav?: boolean;
  provideTheme?: boolean;
  dark?: boolean;
}) {
  const shellStyle = {
    ...DISPLAY,
    ...(dark ? { "--theme-surface-dark": EDITORIAL_DARK_SURFACE } : {}),
    backgroundColor: dark ? SURFACE_DARK : GROUND,
    color: dark ? GROUND : INK,
  } as CSSProperties;

  const shell = (
    <div className="antialiased" data-editorial="" style={shellStyle}>
      <a
        href="#main-content"
        className="fixed top-2 left-2 z-[100] -translate-y-20 bg-[var(--theme-surface-dark)] px-4 py-3 text-sm font-semibold text-[var(--theme-ground)] transition-transform focus:translate-y-0 motion-reduce:transition-none"
      >
        Skip to content
      </a>
      <EditorialNav
        wordmark="MLX"
        showSectors
        links={SITE_NAV_LINKS}
        cta={SITE_CTA}
        themePicker
        solidFromStart={solidNav}
        dark={dark}
      />
      {/* The single <main> landmark for every editorial page + skip-link
          target. The home and /concepts render their content as a plain
          <div> so this stays the one main region per page. Bare element (no
          className) so it is layout-neutral — <main> is display:block. */}
      <main id="main-content">{children}</main>
      <WaitlistDialog />
      <NewsletterDialog />
      <EditorialFooter
        dark={dark}
        wordmark="MLX"
        blurb="The AI Twin: governed business context built from your systems, proven on your own data and used through the AI tools you approve. Built and deployed by Mercury Labs."
        columns={SITE_FOOTER_COLUMNS}
        legal="© 2026 Mercury Labs. MLX is a Mercury Labs product. All rights reserved."
      />
    </div>
  );

  if (!provideTheme) return shell;
  return (
    <EditorialThemeProvider defaultThemeId={HOME_DEFAULT_THEME_ID}>
      {shell}
    </EditorialThemeProvider>
  );
}

/**
 * Lightweight page header for sub-pages — section marker + display title
 * + optional lead, on the cream ground, with top padding to clear the
 * fixed nav. (The home uses the full HeroSection instead.)
 */
export function PageHeader({
  marker,
  title,
  lead,
  dark = false,
  anchorId = "top",
}: {
  marker: string;
  title: string;
  lead?: string;
  dark?: boolean;
  /** Override when multiple page-header concepts share one review page. */
  anchorId?: string;
}) {
  const bg = dark ? SURFACE_DARK : GROUND;
  const fg = dark ? GROUND : INK;
  const accent = dark ? "var(--theme-accent)" : ACCENT_TEXT;

  return (
    <section
      id={anchorId}
      className="relative overflow-hidden"
      style={{ backgroundColor: bg, color: fg }}
    >
      <Grain opacity={dark ? 0.5 : 0.45} />
      <div className={`relative z-10 pt-28 pb-12 sm:pt-32 sm:pb-14 md:pt-40 md:pb-16 ${EDITORIAL_CONTAINER_CLS}`}>
        <div className="flex items-center gap-3">
          <span
            className="h-2 w-2"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          />
          <span className={LABEL_CLS} style={{ color: accent }}>
            {marker}
          </span>
        </div>
        <h1
          style={{ ...DISPLAY, color: fg }}
          className="mt-8 max-w-[20ch] text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.03em] sm:text-[3.25rem] md:text-[4.25rem]"
        >
          {title}
        </h1>
        {lead && (
          <p
            className="mt-6 max-w-[58ch] text-[1.1rem] leading-[1.6] md:text-[1.25rem]"
            style={{
              color: dark ? mix("--theme-ground", 88) : mix("--theme-ink", 88),
            }}
          >
            {lead}
          </p>
        )}
      </div>
    </section>
  );
}
